'use client';

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';

export type NavHighlightKey = 'materialanfrage' | 'favoriten' | 'projekte';

interface NavHighlightContextValue {
  highlights: Set<NavHighlightKey>;
  markHighlight: (key: NavHighlightKey) => void;
  clearHighlight: (key: NavHighlightKey) => void;
}

const NavHighlightContext = createContext<NavHighlightContextValue | null>(null);

export function NavHighlightProvider({ children }: { children: ReactNode }) {
  const [highlights, setHighlights] = useState<Set<NavHighlightKey>>(
    () => new Set(),
  );

  const markHighlight = useCallback((key: NavHighlightKey) => {
    setHighlights((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const clearHighlight = useCallback((key: NavHighlightKey) => {
    setHighlights((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  return (
    <NavHighlightContext.Provider
      value={{ highlights, markHighlight, clearHighlight }}
    >
      {children}
    </NavHighlightContext.Provider>
  );
}

export function useNavHighlight(): NavHighlightContextValue {
  const ctx = useContext(NavHighlightContext);
  if (!ctx) {
    throw new Error(
      'useNavHighlight must be used within a NavHighlightProvider',
    );
  }
  return ctx;
}
